import React, { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';

import Global from '../../store/global-selectors';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { InsuranceProviders } from '../../enums/insurance-providers';
import { usePrimarySellerLabelProviderIdForService } from '../../hooks/use-primary-seller-label-provider-for-service';
import * as Actions from '../../store/csw/csw.actions';
import { useOnComponentMount } from '../../hooks/use-on-component-mount';
import { EXTERNAL_FULFILLMENTS, LOCAL_DELIVERY, STANDARD } from '../../enums/csw-types';
import { useIsServiceLocalDelivery } from '../../hooks/use-is-service-local-delivery';

import { FulfillmentPlannerContextProvider } from '../context';
import { getShipFromIdOrDefault } from '../helpers/get-ship-from';
import { setupExternalShipFromIdForContext } from '../context/external-ship-from-setup';
import { useConfigurePackages } from '../hooks/use-configure-packages';
import { useGetValidConfirmationForService } from '../hooks/use-get-valid-confirmation-for-service';
import { useCSWTypeId } from '../hooks/use-csw-type-id';
import { calculateShipFromIdAndSellerFulfillmentProviderId } from '../helpers/caclulate-ship-from-seller-fulfillment-provider-ids';
import { CustomsService } from '../../services/customs-service';
import { filterDutiesOptions } from '../duties-and-taxes/helpers';
import { useSaveEntitiesUpdate } from '../hooks/use-save-entities-update';
import { useSubscribableState, cheapestRateState } from '../../subscribable-state';
import { useEnableStoreCurrencyForCustomsForm } from '../../feature-flags/hooks/use-enable-store-currency-for-customs-form';
import { getCustomsItemsCurrencyCode } from '../helpers/calculate-customs';

const useSelectEntityProperty = (fulfillmentPlanId, property) => {
  return useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property,
    }),
  );
};

export const createArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter((val) => val !== null);
  }
  if (!value) {
    return emptyArray;
  }
  return [value];
};

export const getNewCswTypeId = (_, __, sellerFulfillmentProviderId, isLocalDelivery) => {
  if (sellerFulfillmentProviderId) {
    return EXTERNAL_FULFILLMENTS;
  }
  if (isLocalDelivery) {
    return LOCAL_DELIVERY;
  }
  return STANDARD;
};

const availableShippingOptionsSelectorFactory = () =>
  createSelector(
    (availableShippingOptions) => {
      try {
        return JSON.stringify(availableShippingOptions);
      } catch {
        return '[]';
      }
    },
    (stringAvailableShippingOptions) => {
      try {
        const availableShippingOptions = JSON.parse(stringAvailableShippingOptions);
        const availableDutiesOptions = filterDutiesOptions(availableShippingOptions);
        return {
          availableShippingOptions,
          availableDutiesOptions,
        };
      } catch {
        return {
          availableShippingOptions: [],
          availableDutiesOptions: [],
        };
      }
    },
  );

export const EntitiesAdapterForFulfillmentPlannerContext = React.memo(
  ({ children, fulfillmentPlanId, isReadOnly = false, parentComponentName = '' }) => {
    const rateError = useSelectEntityProperty(fulfillmentPlanId, 'rateSummary.rateError');
    // memoize this?
    const cleanedError = createArrayValue(rateError);
    const rateWarnings = createArrayValue(useSelectEntityProperty(fulfillmentPlanId, 'rateSummary.rate.rateWarnings'));
    const cleanedWarnings = rateWarnings.length ? rateWarnings.map((warn) => warn.warning) : emptyArray;
    const estimatedDeliveryTime = useSelectEntityProperty(fulfillmentPlanId, 'rateSummary.rate.estimatedDelivery');
    const rateTotalCost = useSelectEntityProperty(fulfillmentPlanId, 'rateSummary.rate.totalCost');
    const estimatedPickup = useSelectEntityProperty(fulfillmentPlanId, 'rateSummary.rate.estimatedPickup');

    const fulfillmentServiceId = useSelectEntityProperty(fulfillmentPlanId, 'fulfillmentServiceId');
    let externalShipFromId = useSelectEntityProperty(fulfillmentPlanId, 'sellerFulfillmentProviderId');
    if (externalShipFromId) {
      externalShipFromId = setupExternalShipFromIdForContext(externalShipFromId);
    }
    const shipFromId = useSelectEntityProperty(fulfillmentPlanId, 'shipFromId');
    const shipFroms = useSelector(Global.selectSellerShipFroms);

    const shipDate = useSelectEntityProperty(fulfillmentPlanId, 'shipDate');

    const insuranceProviderEnum =
      InsuranceProviders[useSelectEntityProperty(fulfillmentPlanId, 'insuranceProvider')] || InsuranceProviders.None;

    const configurePackages = useConfigurePackages();
    const existingPackages = useSelectEntityProperty(fulfillmentPlanId, 'packages') || emptyArray;

    const availableSellerShippingServices = useSelectEntityProperty(fulfillmentPlanId, 'availableSellerShippingServices') || emptyArray;
    const serviceCollectionGuid = useSelectEntityProperty(fulfillmentPlanId, 'labelConfiguration.serviceCollectionGuid');
    const serviceId = useSelectEntityProperty(fulfillmentPlanId, 'labelConfiguration.serviceId');
    const sellerLabelProviderId = useSelectEntityProperty(fulfillmentPlanId, 'labelConfiguration.sellerLabelProviderId');
    const primarySellerLabelProviderId = usePrimarySellerLabelProviderIdForService(serviceId);
    const actualSellerLabelProviderId = sellerLabelProviderId || primarySellerLabelProviderId || null;

    const dispatch = useDispatch();
    useOnComponentMount(() => {
      if (serviceId && !sellerLabelProviderId) {
        dispatch(
          Actions.updateShipmentConfiguration(fulfillmentPlanId, {
            sellerLabelProviderId: actualSellerLabelProviderId,
          }),
        );
      }
    });

    const restrictions = useSelectEntityProperty(fulfillmentPlanId, 'restrictions') || emptyObject;

    const availableShippingOptionsSelector = useMemo(availableShippingOptionsSelectorFactory, []);
    const { availableShippingOptions, availableDutiesOptions } = useSelector((state) => {
      const unmemoizedAvailableShippingOptions = Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId,
        property: 'availableShippingOptions',
      });
      return availableShippingOptionsSelector(unmemoizedAvailableShippingOptions);
    });
    useEffect(() => {
      // we can delete this once payload from context is used instead of csw reducer
      dispatch(
        Actions.updateShipmentConfiguration(fulfillmentPlanId, {
          availableShippingOptions,
        }),
      );
    }, [dispatch, availableShippingOptions, fulfillmentPlanId]);

    const isLocalDelivery = useIsServiceLocalDelivery(serviceId);
    const initialCSWTypeId = useMemo(() => {
      return getNewCswTypeId(undefined, undefined, externalShipFromId, isLocalDelivery);
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { updateCSWTypeId } = useCSWTypeId(fulfillmentPlanId, initialCSWTypeId, getNewCswTypeId);

    const getValidConfirmationForService = useGetValidConfirmationForService();
    const otherShippingOptions = useSelectEntityProperty(fulfillmentPlanId, 'labelConfiguration.options') || emptyObject;
    const externalFulfillmentOptions =
      useSelectEntityProperty(fulfillmentPlanId, 'externalFulfillmentConfiguration.additionalOptions') || emptyObject;
    const memoizedOtherShippingOptions = useMemo(() => {
      if (initialCSWTypeId === EXTERNAL_FULFILLMENTS) {
        return {
          ...externalFulfillmentOptions,
        };
      }
      if (!Object.keys(otherShippingOptions).length) {
        return emptyObject;
      }
      const validConfirmationForService = getValidConfirmationForService(serviceId, otherShippingOptions.confirmationEnumId?.toString());
      const updatedOtherShippingOptions = {
        ...otherShippingOptions,
      };

      if (otherShippingOptions.confirmationEnumId?.toString() !== validConfirmationForService) {
        updatedOtherShippingOptions.confirmation = validConfirmationForService;
        updatedOtherShippingOptions.confirmationEnumId = validConfirmationForService;
      }
      return updatedOtherShippingOptions;
    }, [otherShippingOptions, getValidConfirmationForService, serviceId, externalFulfillmentOptions, initialCSWTypeId]);

    const driverTip = useSelectEntityProperty(fulfillmentPlanId, 'labelConfiguration.driverTip');
    const pickup = useSelectEntityProperty(fulfillmentPlanId, 'labelConfiguration.pickup');

    const customs = useSelectEntityProperty(fulfillmentPlanId, 'customs') || emptyObject;

    const needsCustoms = useSelectEntityProperty(fulfillmentPlanId, 'properties.needsCustoms');
    const customsReason = useSelectEntityProperty(fulfillmentPlanId, 'properties.reason');
    const isInternational = useSelector((state) =>
      Global.selectEntitiesFulfillmentPlanIsInternational(state, {
        fulfillmentPlanId,
      }),
    );

    const shipTo = useSelectEntityProperty(fulfillmentPlanId, 'shipTo');

    const shipmentEmailNotificationTemplateId = useSelectEntityProperty(fulfillmentPlanId, 'shipmentEmailNotificationTemplateId');
    const deliveryEmailNotificationTemplateId = useSelectEntityProperty(fulfillmentPlanId, 'deliveryEmailNotificationTemplateId');
    const packingSlipTemplateId = useSelectEntityProperty(fulfillmentPlanId, 'packingSlipTemplateId');

    const cswTypeId = useMemo(() => {
      return updateCSWTypeId(shipFromId, externalShipFromId, serviceId);
    }, [shipFromId, externalShipFromId, serviceId, updateCSWTypeId]);

    const [bestWayToShip, setBestWayToShip] = useSubscribableState(cheapestRateState(fulfillmentPlanId, {}));
    useOnComponentMount(() => {
      dispatch(Actions.getBestWayToShipOnMount(fulfillmentPlanId, { setBestWayToShip }, cswTypeId));
    });

    const { saveEntitiesUpdate } = useSaveEntitiesUpdate(fulfillmentPlanId);
    const saveConfiguration = useCallback(
      (payload, options = {}, sendAnalytics) => {
        if (isReadOnly) {
          return;
        }

        const { shipFromId: internalShipFromId, sellerFulfillmentProviderId } = calculateShipFromIdAndSellerFulfillmentProviderId(
          getShipFromIdOrDefault(payload.shipFromId || shipFromId || externalShipFromId, shipFroms),
        );

        const refinedPayload = {
          ...payload,
          shipFromId: internalShipFromId,
          sellerFulfillmentProviderId,
          setBestWayToShip,
        };
        const newCswTypeId = updateCSWTypeId(internalShipFromId, sellerFulfillmentProviderId, payload?.serviceId || serviceId);

        saveEntitiesUpdate(refinedPayload, newCswTypeId, options, sendAnalytics);
      },
      [isReadOnly, shipFromId, shipFroms, serviceId, externalShipFromId, updateCSWTypeId, saveEntitiesUpdate, setBestWayToShip],
    );

    const triggerRate = useCallback(() => {
      saveConfiguration({});
    }, [saveConfiguration]);

    const packages = useMemo(() => configurePackages(existingPackages), [configurePackages, existingPackages]);
    const serviceIdAndSellerLabelProviderId = useMemo(() => {
      return { serviceId, sellerLabelProviderId: actualSellerLabelProviderId };
    }, [serviceId, actualSellerLabelProviderId]);

    const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || 'USD';
    const safeDriverTip = useMemo(() => {
      if (!driverTip) {
        return { value: 0, code: sellerCurrencyCode };
      }
      return driverTip;
    }, [driverTip, sellerCurrencyCode]);

    const defaultCountryCode = useSelector(Global.selectSellerHomeCountry);
    const defaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
    const defaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || 'USD';
    const defaultCustomsValues = useSelector(Global.selectSellerDefaultCustomData);
    const enableStoreCurrencyForCustomsForm = useEnableStoreCurrencyForCustomsForm();
    const customsCurrency = getCustomsItemsCurrencyCode(customs, defaultCurrencyCode, enableStoreCurrencyForCustomsForm);

    const safeCustoms = useMemo(() => {
      let sanitizedCustoms = customs;

      if (enableStoreCurrencyForCustomsForm && customs.customsItems && customs.customsItems.length > 0) {
        sanitizedCustoms = {
          ...customs,
          customsItems: customs.customsItems.map((item) => ({
            ...item,
            unitPrice: {
              ...item.unitPrice,
              code: item.unitPrice?.code || customsCurrency,
            },
          })),
        };
      }

      const initialValidatedCustoms = CustomsService.validateCustoms(
        sanitizedCustoms,
        defaultCountryCode,
        customsCurrency,
        defaultWeightUnit,
      );
      return CustomsService.fillCustomsWithDefaults(initialValidatedCustoms, defaultCustomsValues, defaultCurrencyCode, defaultWeightUnit);
    }, [
      customs,
      enableStoreCurrencyForCustomsForm,
      defaultCountryCode,
      customsCurrency,
      defaultWeightUnit,
      defaultCustomsValues,
      defaultCurrencyCode,
    ]);

    return (
      <FulfillmentPlannerContextProvider
        availableDutiesOptions={availableDutiesOptions}
        availableSellerShippingServices={availableSellerShippingServices}
        bestWayToShip={bestWayToShip}
        canSyncWithEntities
        cswTypeId={cswTypeId}
        customs={safeCustoms}
        customsReason={customsReason}
        deliveryEmailNotificationTemplateId={deliveryEmailNotificationTemplateId}
        driverTip={safeDriverTip}
        estimatedDeliveryTime={estimatedDeliveryTime}
        fulfillmentPlanId={fulfillmentPlanId}
        fulfillmentServiceId={fulfillmentServiceId}
        initialEstimatedPickup={estimatedPickup}
        initialRateError={cleanedError}
        insuranceProvider={insuranceProviderEnum}
        isInternational={isInternational}
        needsCustoms={needsCustoms}
        onConfigurationChange={saveConfiguration}
        otherShippingOptions={memoizedOtherShippingOptions}
        packages={packages}
        packingSlipTemplateId={packingSlipTemplateId}
        parentComponentName={parentComponentName}
        pickup={pickup}
        rateTotalCost={rateTotalCost}
        restrictions={restrictions}
        serviceCollectionGuid={serviceCollectionGuid}
        serviceIdAndSellerLabelProviderId={serviceIdAndSellerLabelProviderId}
        shipDate={shipDate}
        shipFromId={getShipFromIdOrDefault(shipFromId || externalShipFromId, shipFroms)}
        shipmentEmailNotificationTemplateId={shipmentEmailNotificationTemplateId}
        shipTo={shipTo}
        shouldShowDutiesAndTaxes={!!availableDutiesOptions.length}
        shouldSyncOnBulkUpdate
        triggerRate={triggerRate}
        updatedRateWarnings={cleanedWarnings}
      >
        {children}
      </FulfillmentPlannerContextProvider>
    );
  },
);

EntitiesAdapterForFulfillmentPlannerContext.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  fulfillmentPlanId: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool,
  parentComponentName: PropTypes.string,
};
